import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, concatMap, tap } from 'rxjs';

import { environment } from '../../../environments/environment';
import { LoginRequest } from '../models/user';
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { RedirectRequest } from '@azure/msal-browser';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  currentUserName$!: BehaviorSubject<string>;

  private loginSsoUrl = `${environment.LOGIN_SSO_URL}`;
  private staticDataUrl  = `${environment.BASE_URL}${environment.BASE_API.STATIC}`;

  constructor(
    private http: HttpClient,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalService: MsalService
  ) {
    this.currentUserName$ = new BehaviorSubject<string>(sessionStorage.getItem('current-username') ?? '');
  }

  loginSso() {
    if (this.msalGuardConfig.authRequest) {
      this.msalService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
    } else {
      this.msalService.loginRedirect();
    }
  }

  login(loginData: LoginRequest) {
    return this.http
      .post(this.loginSsoUrl, loginData)
      .pipe(
        concatMap((response: any) => {
          this.currentUserName$.next(response.user['USERNAME']);
          sessionStorage.setItem('current-username', response.user['USERNAME']);
          if (response?.user?.['EMAIL']) {
            sessionStorage.setItem('current-email', response.user['EMAIL']);
          }
          sessionStorage.setItem('id-token', response.idtoken);
          return this.http.get(this.staticDataUrl);
        }),
        tap((staticDataResp: any) => {
          sessionStorage.setItem('SCREENS', JSON.stringify(staticDataResp.body.SCREENS ?? []));
        })
      );
  }

  logout() {
    sessionStorage.removeItem('msal-access-token');
    sessionStorage.removeItem('msal-id-token');
    sessionStorage.clear();
    this.currentUserName$.next('');
  }

  /**
   * Checks if user has valid MSAL tokens in sessionStorage
   * Validates token structure and expiry timestamp
   */
  isMsalLoggedIn(): boolean {
    const msalAccessToken = sessionStorage.getItem('msal-access-token');
    const msalIdToken = sessionStorage.getItem('msal-id-token');

    if (msalAccessToken && msalIdToken) {
      try {
        const tokenData = JSON.parse(msalAccessToken);
        if (tokenData.expiry) {
          const expiryTime = new Date(tokenData.expiry).getTime();
          const now = new Date().getTime();
          return expiryTime > now; // Check if token is not expired
        }
        return false;
      } catch (error) {
        return false; // Invalid token structure
      }
    }
    return false;
  }
}


